import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from './cn';

/**
 * Input size — Linear/Geist-dense 컨트롤 높이 (DESIGN-SPEC §6, §10).
 * sm=28px(필터바·인라인), md=32px(폼·툴바 기본), lg=40px(강조·모바일 터치).
 * 미지정 시 md. 높이는 --control-h-* 토큰을 참조한다(전면개편: 단일 h-10=40 → 가변, 기본 32).
 * Button 의 size 패턴과 동형 — 폼 페이지 전반이 채택하는 표준 컨트롤.
 */
export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** 컨트롤 높이. 기본 'md'(32px). */
  size?: InputSize;
}

const SIZE_HEIGHT: Record<InputSize, string> = {
  sm: 'var(--control-h-sm)',
  md: 'var(--control-h-md)',
  lg: 'var(--control-h-lg)',
};

const SIZE_CLASS: Record<InputSize, string> = {
  sm: 'px-2.5 text-[13px]',
  md: 'px-3 text-[13px]',
  lg: 'px-3.5 text-[14px]',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, style, size = 'md', ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        'flex w-full rounded-md border transition-colors',
        SIZE_CLASS[size],
        'placeholder:text-[color:var(--content-soft)]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]',
        className,
      )}
      style={{
        height: SIZE_HEIGHT[size],
        background: 'var(--surface-0)',
        color: 'var(--content-strong)',
        borderColor: 'var(--divider-strong)',
        ...style,
      }}
      {...props}
    />
  ),
);
Input.displayName = 'Input';
